import { api, ApiError } from "../core/api.js";
import { h, mount } from "../core/dom.js";
import { isAuthenticated } from "../core/tokens.js";
import { navigate } from "../core/router.js";
import { t } from "../core/i18n.js";
import { emptyState, spinner } from "../core/states.js";
import type { Cut, Page, Variant } from "../models.js";

/**
 * Library / my account (04-web §2.6, 01 §3.U).
 * GET /v1/auth/me + GET /v1/me/cuts + GET /v1/me/variants (best effort).
 * Endpoints not yet implemented degrade to a graceful empty state.
 */

interface Me {
  id: string;
  handle: string;
  display_name?: string;
  role?: string;
  bio?: string | null;
}

export async function libraryView(_params: Record<string, string>, root: HTMLElement): Promise<void> {
  if (!isAuthenticated()) {
    navigate("/login");
    return;
  }
  mount(root, spinner());

  const me = await safe<Me>("/v1/auth/me");
  const cuts = await safe<Page<Cut>>("/v1/me/cuts");
  const variants = await safe<Page<Variant>>("/v1/me/variants");

  const header = me
    ? h(
        "div",
        { class: "card", style: "padding:16px;margin-bottom:16px" },
        h("h1", { style: "margin:0 0 6px" }, me.display_name ?? me.handle),
        h("p", { class: "muted", style: "margin:0" }, `@${me.handle}`, me.role ? ` · ${me.role}` : ""),
        me.bio ? h("p", { style: "margin:8px 0 0" }, me.bio) : null,
      )
    : h("div", { class: "card", style: "padding:16px;margin-bottom:16px" }, h("p", { class: "error", style: "margin:0" }, t("library.profileFailed")));

  mount(
    root,
    h(
      "section",
      {},
      header,
      h("h3", {}, t("library.myCuts")),
      listSection(cuts?.items ?? [], (c) => h("span", {}, c.name ?? c.id), t("library.noCuts")),
      h("h3", {}, t("library.myVariants")),
      listSection(
        variants?.items ?? [],
        (v) => h("span", {}, `${v.id} · [${v.start_ms}–${v.end_ms})`),
        t("library.noVariants"),
      ),
    ),
  );
}

function listSection<T>(items: T[], render: (item: T) => Node, empty: string): HTMLElement {
  if (items.length === 0) return emptyState(empty);
  return h(
    "div",
    {},
    ...items.map((item) => h("div", { class: "card", style: "padding:12px;margin:8px 0" }, render(item))),
  );
}

async function safe<T>(path: string): Promise<T | null> {
  try {
    return await api.get<T>(path);
  } catch (e) {
    if (e instanceof ApiError) return null; // missing/forbidden endpoint → graceful
    return null;
  }
}
